import React from 'react';
import { observer } from 'mobx-react';
import { FormattedMessage } from 'react-intl';
import DefaultButton from '../../../../../../global/default-button/default-button';
import { FormField } from '../../../../../../global/form/form-field/form-field';
import { OEToggle } from '../../../../../../global/oe-toggle/oe-toggle';
import { CommonModal } from '../../../../../../global/modal/common/common-modal';
import { APPLY_TEXT, CANCEL_TEXT } from '../../../../../../../constants/common-labels.constants';
import {
    HUB_REGISTRATION_EDIT_FIELD_MODAL_TITLE_TEXT,
    HUB_REGISTRATION_FIELD_DISPLAYED_TOGGLE_TEXT,
    HUB_REGISTRATION_FIELD_REQUIRED_TOGGLE_TEXT,
} from '../../../../../event-hubs.text';
import { HubRegistrationSettingsVM } from './hub-registration-settings.viewmodel';
import './hub-registration-field-modal.scss';

type HubRegistrationFieldModalProps = {
    vm: HubRegistrationSettingsVM;
};

export const HubRegistrationFieldModal: React.FC<HubRegistrationFieldModalProps> = observer(({ vm }) => {
    const draft = vm.getEditingFieldDraft();

    if (!draft) {
        return null;
    }

    const canEditVisibility = vm.canEditFieldVisibility(draft);
    const canEditMandatory = vm.canEditFieldMandatory(draft);
    const isVisible = vm.isEditingFieldVisible();
    const isMandatory = vm.isEditingFieldMandatory();

    return (
        <CommonModal
            isOpen
            onRequestClose={vm.closeFieldEditor}
            overlayClassName='hub-registration-field-editor-overlay'
            className='hub-registration-field-editor__modal'
            classNameBody='hub-registration-field-editor__body-wrapper'
            title={
                <FormattedMessage
                    {...HUB_REGISTRATION_EDIT_FIELD_MODAL_TITLE_TEXT}
                    values={{ fieldName: draft.defaultFieldTitle }}
                />
            }
            footer={
                <div className='hub-registration-field-editor__footer'>
                    <DefaultButton
                        modifier='cancel'
                        text={<FormattedMessage {...CANCEL_TEXT} />}
                        onClick={vm.closeFieldEditor}
                    />
                    <DefaultButton
                        text={<FormattedMessage {...APPLY_TEXT} />}
                        onClick={vm.applyFieldEditor}
                    />
                </div>
            }
        >
            <div className='hub-registration-field-editor__body'>
                <FormField
                    label={<FormattedMessage
                        id='oe.label.fieldLabel'
                        description='Label text'
                        defaultMessage='Field Label'
                    />}
                    className='hub-registration-field-editor__label-field'
                >
                    <input
                        className='oe-form-field__control'
                        value={draft.label}
                        type='text'
                        onChange={event => vm.setEditingFieldLabel(event.target.value)}
                    />
                    {vm.getEditingFieldLabelError() && (
                        <span className='oe-form-field__error'>{vm.getEditingFieldLabelError()}</span>
                    )}
                </FormField>

                {canEditVisibility && (
                    <div className='hub-registration-field-editor__toggle-row'>
                        <OEToggle
                            isSelected={isVisible}
                            onClick={vm.toggleEditingFieldVisibility}
                        />
                        <FormattedMessage {...HUB_REGISTRATION_FIELD_DISPLAYED_TOGGLE_TEXT} />
                    </div>
                )}

                {canEditMandatory && (
                    <div className='hub-registration-field-editor__toggle-row'>
                        <OEToggle
                            isSelected={isMandatory}
                            onClick={vm.toggleEditingFieldMandatory}
                            isDisabled={!isVisible}
                        />
                        <FormattedMessage {...HUB_REGISTRATION_FIELD_REQUIRED_TOGGLE_TEXT} />
                    </div>
                )}
            </div>
        </CommonModal>
    );
});
